import { House, Maximize2, Waves } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	EXTERIOR_TREATMENT_CATALOG,
	INTERIOR_WOOD_CATALOG,
} from "../../domain/finishes/finishCatalog";
import type { FinishPriceDeltas } from "../../domain/finishes/finishPriceDeltas";
import type { FinishConfig } from "../../domain/finishes/types";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import { DEFAULT_SITE_CONDITIONS } from "../../domain/foundation/siteConditions";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import type { InteriorLayout } from "../../domain/interior";
import type { HeaterModelFit } from "../../domain/heater/heaterCatalog";
import {
	type IndoorRoomConfig,
	normalizeIndoorRoom,
} from "../../domain/indoor/indoorRoom";
import type {
	ClimateProfile,
	LocationMode,
	Market,
} from "../../domain/location/types";
import {
	getPartitionSplitBounds,
	normalizePartitionSplit,
} from "../../domain/partition/partitionSplit";
import { getRoofConfigSummaryLabel } from "../../domain/roof/roofConfig";
import { resolveShowerFootprint } from "../../engines/interiorFixtures";
import { resolveTwoRoomConfig } from "../../engines/twoRoomSauna";
import type {
	DeckConfig,
	RoofConfig,
	SaunaDimensions,
	ThermalInputs,
	TwoRoomConfig,
} from "../../engines/types";
import { useT } from "../../i18n";
import type { BuilderRunningCostSummary } from "../../pages/builder/runningCostSummary";
import {
	getMatchingTwoRoomPreset,
	type TwoRoomPresetId,
} from "../../store/twoRoomPresets";
import type { SaunaType } from "../../types/design";
import { DimensionSliders } from "./DimensionSliders";
import { FinishesEditor } from "./FinishesEditor";
import { IndoorRoomEditor } from "./IndoorRoomEditor";
import { OpeningsEditor } from "./OpeningsEditor";
import { RoofConfigEditor } from "./RoofConfigEditor";
import { SiteConditionsEditor } from "./SiteConditionsEditor";
import { ThermalInputsEditor } from "./ThermalInputsEditor";
import { TwoRoomAdvancedPickers } from "./TwoRoomAdvancedPickers";
import { TwoRoomPresetCard } from "./TwoRoomPresetCard";
import { DeckEditor } from "./tabs/DeckEditor";

type DesignSection =
	| "layout"
	| "dimensions"
	| "openings"
	| "deck"
	| "roof"
	| "site"
	| "character"
	| "thermal"
	| "room"
	| "equipment";

interface DimensionPanelProps {
	saunaType: SaunaType;
	dimensions: SaunaDimensions;
	partitionSplit: number;
	twoRoomConfig?: TwoRoomConfig;
	openings: WallOpening[];
	deckConfig: DeckConfig | null;
	roofConfig: RoofConfig;
	thermalInputs: ThermalInputs;
	interiorLayout?: InteriorLayout | null;
	finishes: FinishConfig;
	/** Chosen wall height (issue 155); `null` = the default. */
	wallHeightM?: number | null;
	/** Inomhus only (issues 174/175). */
	indoorRoom?: IndoorRoomConfig | null;
	onIndoorRoomChange?: (indoorRoom: IndoorRoomConfig) => void;
	/** Per-option cost delta for the character swatches, from the live BOM. */
	finishPriceDeltas: FinishPriceDeltas;
	/** Live heat-up/session-cost readout shown beside the heater choice. */
	runningCost?: BuilderRunningCostSummary | null;
	/** Whether the chosen catalog heater suits the room (issue 159). */
	heaterModelFit?: HeaterModelFit;
	siteConditions: SiteConditions;
	market: Market;
	locationMode: LocationMode;
	climateProfile: ClimateProfile;
	postalCode: string;
	onDimensionsChange: (d: SaunaDimensions) => void;
	onPartitionSplitChange: (split: number) => void;
	onTwoRoomConfigChange?: (config: Partial<TwoRoomConfig>) => void;
	onTwoRoomPresetChange?: (preset: TwoRoomPresetId) => void;
	onOpeningsChange: (openings: WallOpening[]) => void;
	onDeckConfigChange: (config: DeckConfig | null) => void;
	onRoofConfigChange: (config: RoofConfig) => void;
	onThermalInputsChange: (thermal: ThermalInputs) => void;
	onFinishesChange: (finishes: Partial<FinishConfig>) => void;
	onWallHeightChange?: (wallHeightM: number) => void;
	onSiteConditionsChange: (conditions: SiteConditions) => void;
	onClimateProfileChange: (profile: ClimateProfile) => void;
	onPostalCodeChange: (code: string) => void;
	onStartWindowDrag?: (widthMm: number, heightMm: number) => void;
	isDraggingWindow?: boolean;
	onActiveSectionChange?: (section: DesignSection) => void;
}

export function DimensionPanel({
	saunaType,
	dimensions,
	partitionSplit,
	twoRoomConfig = resolveTwoRoomConfig(),
	openings,
	deckConfig,
	roofConfig,
	thermalInputs,
	interiorLayout,
	finishes,
	wallHeightM = null,
	indoorRoom = null,
	onIndoorRoomChange,
	finishPriceDeltas,
	runningCost,
	heaterModelFit,
	siteConditions = DEFAULT_SITE_CONDITIONS,
	market,
	locationMode,
	climateProfile,
	postalCode,
	onDimensionsChange,
	onPartitionSplitChange,
	onTwoRoomConfigChange = () => undefined,
	onTwoRoomPresetChange = () => undefined,
	onOpeningsChange,
	onDeckConfigChange,
	onRoofConfigChange,
	onThermalInputsChange,
	onFinishesChange,
	onWallHeightChange,
	onSiteConditionsChange,
	onClimateProfileChange,
	onPostalCodeChange,
	onStartWindowDrag,
	isDraggingWindow,
	onActiveSectionChange,
}: DimensionPanelProps) {
	const t = useT();
	const getDeckShapeLabel = useCallback(
		(shape: DeckConfig["shape"]) => {
			switch (shape) {
				case "l-shape":
					return t.deck_shape_l_shape;
				case "wrap-front":
					return t.deck_shape_wrap_front;
				case "wrap-sides":
					return t.deck_shape_wrap_sides;
				case "rectangular":
				default:
					return t.deck_shape_rectangular;
			}
		},
		[t],
	);
	const [activeSection, setActiveSection] = useState<DesignSection>(() =>
		saunaType === "tvarum" ? "layout" : "dimensions",
	);
	const [advancedOpen, setAdvancedOpen] = useState(false);
	const area = dimensions.width * dimensions.length;
	const supportsSiteConditions =
		saunaType === "fyrkantig" || saunaType === "tvarum";
	const supportsRoofControls =
		saunaType === "fyrkantig" || saunaType === "tvarum";
	const supportsTwoRoomLayout = saunaType === "tvarum";
	// An indoor sauna stands in an existing room: nothing to build a deck on.
	const supportsDeck = saunaType !== "inomhus";
	const supportsIndoorRoom = saunaType === "inomhus" && !!onIndoorRoomChange;
	// A basement ceiling is as real a constraint as a roof: the indoor sauna
	// gets the wall-height control too, just no roof shape.
	const supportsWallHeight = supportsRoofControls || saunaType === "inomhus";
	const resolvedIndoorRoom = normalizeIndoorRoom(indoorRoom);
	const supportsPartitionSplit =
		supportsTwoRoomLayout && twoRoomConfig.hasRelaxRoom;

	const openingSummary = useMemo(() => {
		const doors = openings.filter((opening) => opening.type === "door").length;
		const windows = openings.filter(
			(opening) => opening.type === "window",
		).length;

		if (openings.length === 0) {
			return t.dim_openingSummaryNone;
		}

		const parts = [];
		if (doors > 0)
			parts.push(
				`${doors} ${doors > 1 ? t.dim_openingDoorPlural : t.dim_openingDoorSingular}`,
			);
		if (windows > 0)
			parts.push(
				`${windows} ${windows === 1 ? t.dim_openingWindow : t.dim_openingWindowPlural}`,
			);
		return parts.join(" • ");
	}, [openings, t]);

	// Header summary intentionally shows only the heater type. The shower
	// and uninsulated-log-cabin toggles are secondary and live in the open
	// section, so ticking them must not grow/rewrap the collapsed header.
	const thermalSummary = useMemo(
		() =>
			thermalInputs.heaterType === "wood"
				? t.dim_heaterWood
				: t.dim_heaterElectric,
		[thermalInputs.heaterType, t],
	);

	const roofSummary = useMemo(
		() => getRoofConfigSummaryLabel(roofConfig, market),
		[market, roofConfig],
	);

	// Wood and colour only — the panel direction is the least surprising of the
	// three and would push the collapsed header to a second line in the sidebar.
	const characterSummary = useMemo(
		() =>
			`${t[INTERIOR_WOOD_CATALOG[finishes.interiorWood].labelKey]} • ${
				t[EXTERIOR_TREATMENT_CATALOG[finishes.exteriorTreatment].labelKey]
			}`,
		[finishes.exteriorTreatment, finishes.interiorWood, t],
	);

	const deckSummary = useMemo(() => {
		if (!deckConfig) {
			return t.deck_disabledState;
		}

		return `${deckConfig.depth.toFixed(1)} × ${deckConfig.width.toFixed(1)} m • ${getDeckShapeLabel(deckConfig.shape)}`;
	}, [deckConfig, getDeckShapeLabel, t]);

	const partitionSummary = useMemo(() => {
		if (!supportsPartitionSplit) {
			return null;
		}

		const saunaDepth = dimensions.length * partitionSplit;
		const loungeDepth = dimensions.length - saunaDepth;
		return `${saunaDepth.toFixed(1)} m / ${loungeDepth.toFixed(1)} m`;
	}, [dimensions.length, partitionSplit, supportsPartitionSplit]);

	const partitionBounds = useMemo(
		() =>
			supportsPartitionSplit
				? getPartitionSplitBounds(dimensions.length, {
						heaterType: thermalInputs.heaterType,
						heaterModelId: thermalInputs.heaterModelId,
					})
				: null,
		[
			dimensions.length,
			supportsPartitionSplit,
			thermalInputs.heaterType,
			thermalInputs.heaterModelId,
		],
	);

	const showerFits = useMemo(() => supportsPartitionSplit && resolveShowerFootprint({
		dimensions, openings, partitionSplit, ...twoRoomConfig, roofConfig, interiorLayout,
		thermalInputs, wallHeightM: wallHeightM ?? undefined,
	}) !== null, [supportsPartitionSplit, dimensions, openings, partitionSplit, twoRoomConfig,
		roofConfig, interiorLayout, thermalInputs, wallHeightM]);

	const activePreset = getMatchingTwoRoomPreset(twoRoomConfig);

	const layoutSummary = useMemo(() => {
		const entry =
			twoRoomConfig.entryWall === "long"
				? t.twoRoom_entryWallLong
				: t.twoRoom_entryWallShort;
		const glazing =
			twoRoomConfig.glazingStyle === "panorama"
				? t.twoRoom_glazingPanorama
				: t.twoRoom_glazingStandard;
		const roomMode = twoRoomConfig.hasRelaxRoom
			? t.twoRoom_relaxRoomOnSummary
			: t.twoRoom_relaxRoomOffSummary;
		const saunaSide =
			twoRoomConfig.saunaPosition === "right"
				? t.twoRoom_saunaPositionRight
				: t.twoRoom_saunaPositionLeft;

		return twoRoomConfig.hasRelaxRoom
			? `${entry} • ${glazing} • ${roomMode} • ${saunaSide}`
			: `${entry} • ${glazing} • ${roomMode}`;
	}, [t, twoRoomConfig]);

	const presetCards: Array<{
		id: TwoRoomPresetId;
		title: string;
		description: string;
		ariaLabel: string;
		icon: typeof House;
	}> = [
		{
			id: "classic",
			title: t.twoRoom_presetClassicTitle,
			description: t.twoRoom_presetClassicDescription,
			ariaLabel: t.twoRoom_presetClassicAria,
			icon: House,
		},
		{
			id: "lakeside",
			title: t.twoRoom_presetLakesideTitle,
			description: t.twoRoom_presetLakesideDescription,
			ariaLabel: t.twoRoom_presetLakesideAria,
			icon: Waves,
		},
		{
			id: "openPanorama",
			title: t.twoRoom_presetOpenTitle,
			description: t.twoRoom_presetOpenDescription,
			ariaLabel: t.twoRoom_presetOpenAria,
			icon: Maximize2,
		},
	];

	useEffect(() => {
		onActiveSectionChange?.(activeSection);
	}, [activeSection, onActiveSectionChange]);

	useEffect(() => {
		if (saunaType !== "tvarum" && activeSection === "layout") {
			setActiveSection("dimensions");
		}
	}, [activeSection, saunaType]);

	const siteSummary = useMemo(() => {
		const terrainLabel =
			siteConditions.terrainMode === "flat"
				? t.dim_terrainFlat
				: `${siteConditions.slopeAcrossPercent.toFixed(1)}% × ${siteConditions.slopeAlongPercent.toFixed(1)}%`;

		return `${siteConditions.plinthLengthMm} mm • min ${siteConditions.minBuriedDepthMm} mm • ${terrainLabel}`;
	}, [siteConditions, t]);

	const sectionMetaWithoutEyebrow: Array<{
		id: DesignSection;
		title: string;
		summary: string;
		badge?: string;
	}> = [
		...(supportsTwoRoomLayout
			? [
					{
						id: "layout" as const,
						title: t.twoRoom_sectionLayout,
						summary: layoutSummary,
					},
				]
			: []),
		{
			id: "dimensions",
			title: t.dim_sectionDimensions,
			summary: `${Math.round(dimensions.width * 1000)} × ${Math.round(dimensions.length * 1000)} mm • ${area.toFixed(1)} m²`,
		},
		...(supportsIndoorRoom
			? [
					{
						id: "room" as const,
						title: t.indoor_roomTitle,
						summary:
							resolvedIndoorRoom.existingWalls.length === 0
								? t.indoor_roomSummaryFree
								: t.indoor_roomSummaryWalls.replace(
										"{count}",
										String(resolvedIndoorRoom.existingWalls.length),
									),
					},
				]
			: []),
		{
			id: "openings",
			title: t.dim_sectionOpenings,
			summary: openingSummary,
			badge: `${openings.length}`,
		},
		...(supportsDeck
			? [
					{
						id: "deck" as const,
						title: t.dim_sectionDeck,
						summary: deckSummary,
					},
				]
			: []),
		...(supportsRoofControls
			? [
					{
						id: "roof" as const,
						title: t.dim_sectionRoof,
						summary: roofSummary,
					},
				]
			: []),
		...(supportsSiteConditions
			? [
					{
						id: "site" as const,
						title: t.dim_sectionSite,
						summary: siteSummary,
					},
				]
			: []),
		{
			id: "character",
			title: t.finish_sectionTitle,
			summary: characterSummary,
		},
		...(saunaType !== "tunna" ? [{ id: "equipment" as const, title: t.equipment_title, summary: `${thermalInputs.saunaLighting === "none" ? t.equipment_noLight : t.equipment_wallLight}${supportsPartitionSplit && thermalInputs.includesShower ? ` • ${t.equipment_shower}` : ""}` }] : []),
		{
			id: "thermal",
			title: t.dim_sectionThermal,
			summary: thermalSummary,
		},
	];
	const designGroups = [
		{ label: t.dim_groupSauna, sections: ["layout", "dimensions", "room", "openings", "equipment"] },
		{ label: t.dim_groupAppearance, sections: ["character", "roof", "deck"] },
		{ label: t.dim_groupConditions, sections: ["thermal", "site"] },
	];
	const activeGroup = designGroups.find((group) => group.sections.includes(activeSection))!;
	const sectionMeta = activeGroup.sections.flatMap((id) =>
		sectionMetaWithoutEyebrow.filter((section) => section.id === id),
	);

	return (
		<div className="design-step-content inspector-panel">
			<section className="design-panel-intro" aria-label={t.dim_panelLabel}>
				<h2>{t.dim_panelTitle}</h2>
			</section>

			<nav className="design-category-nav" aria-label={t.dim_panelLabel}>
				{designGroups.map((group) => (
					<button key={group.label} type="button"
						aria-pressed={group === activeGroup}
						onClick={() => setActiveSection(
							group.sections.find((id) => sectionMetaWithoutEyebrow.some((section) => section.id === id)) as DesignSection,
						)}>{group.label}</button>
				))}
			</nav>
			<div className="inspector-sections" aria-label={t.dim_panelLabel}>
				{sectionMeta.map((section) => {
					const isActive = activeSection === section.id;

					return (
						<section
							key={section.id}
							className={`inspector-section${isActive ? " active" : ""}`}
							aria-labelledby={`design-section-${section.id}`}
						>
							<button
								type="button"
								id={`design-section-${section.id}`}
								className="inspector-section-toggle"
								aria-expanded={isActive}
								onClick={() => setActiveSection(section.id)}
							>
								<span className="inspector-section-headings">
									<span className="inspector-section-title">
										{section.title}
									</span>
									<span className="inspector-section-summary">
										{section.summary}
									</span>
								</span>
								{section.badge && (
									<span className="inspector-section-badge">
										{section.badge}
									</span>
								)}
								<span className="inspector-section-arrow">
									{isActive ? "−" : "+"}
								</span>
							</button>

							{isActive && section.id === "dimensions" && (
								<DimensionSliders
									saunaType={saunaType}
									dimensions={dimensions}
									market={market}
									locationMode={locationMode}
									climateProfile={climateProfile}
									postalCode={postalCode}
									onDimensionsChange={onDimensionsChange}
									onClimateProfileChange={onClimateProfileChange}
									onPostalCodeChange={onPostalCodeChange}
									wallHeightM={wallHeightM}
									// Walls exist only on the rectangular engines; a barrel's
									// height is its radius.
									onWallHeightChange={
										supportsWallHeight ? onWallHeightChange : undefined
									}
								/>
							)}

							{isActive && section.id === "layout" && (
								<div className="inspector-section-body">
									<div className="two-room-layout-picker">
										<div className="two-room-layout-picker__intro">
											<p className="section-label">{t.twoRoom_sectionLabel}</p>
											<p>{t.twoRoom_sectionIntro}</p>
										</div>
										<div
											className="two-room-preset-row"
											role="radiogroup"
											aria-label={t.twoRoom_presetGroupAria}
										>
											{presetCards.map((preset) => (
												<TwoRoomPresetCard
													key={preset.id}
													id={preset.id}
													title={preset.title}
													description={preset.description}
													icon={preset.icon}
													selected={activePreset === preset.id}
													ariaLabel={preset.ariaLabel}
													onSelect={onTwoRoomPresetChange}
												/>
											))}
										</div>
										{supportsPartitionSplit && (
											<div className="builder-controls two-room-partition-control">
												<div className="slider-row">
													<div className="slider-label-row">
														<span className="slider-label">
															{t.dim_partitionSplit}
														</span>
														<span className="slider-value">
															{partitionSummary}
														</span>
													</div>
													<input
														type="range"
														className="slider-track"
														min={partitionBounds?.min ?? 0.35}
														max={partitionBounds?.max ?? 0.8}
														step={Math.max(0.001, 0.1 / dimensions.length)}
														value={partitionSplit}
														aria-label={t.dim_partitionSplit}
														onChange={(event) =>
															onPartitionSplitChange(
																normalizePartitionSplit(
																	dimensions.length,
																	Number(event.target.value),
																	{
																		heaterType: thermalInputs.heaterType,
																		heaterModelId: thermalInputs.heaterModelId,
																	},
																),
															)
														}
													/>
												</div>
											</div>
										)}
										<button
											type="button"
											className="two-room-advanced-toggle"
											aria-expanded={advancedOpen}
											onClick={() => setAdvancedOpen((open) => !open)}
										>
											<span>{t.twoRoom_advancedToggle}</span>
											<span aria-hidden="true">{advancedOpen ? "−" : "+"}</span>
										</button>
										{advancedOpen && (
											<TwoRoomAdvancedPickers
												config={twoRoomConfig}
												onChange={onTwoRoomConfigChange}
											/>
										)}
									</div>
								</div>
							)}

							{isActive && section.id === "openings" && (
								<OpeningsEditor
									openings={openings}
									dimensions={dimensions}
									partitionSplit={partitionSplit}
									allowDividerWall={supportsPartitionSplit}
 hasRelaxRoom={supportsPartitionSplit && twoRoomConfig.hasRelaxRoom}
 saunaPosition={twoRoomConfig.saunaPosition}
 roofConfig={roofConfig}
									wallHeightM={wallHeightM ?? undefined}
									onOpeningsChange={onOpeningsChange}
									onStartWindowDrag={onStartWindowDrag}
									isDraggingWindow={isDraggingWindow}
								/>
							)}

							{isActive &&
								section.id === "room" &&
								supportsIndoorRoom &&
								onIndoorRoomChange && (
									<IndoorRoomEditor
										indoorRoom={resolvedIndoorRoom}
										onIndoorRoomChange={onIndoorRoomChange}
									/>
								)}

							{isActive && section.id === "deck" && (
								<DeckEditor
									deckConfig={deckConfig}
									saunaDimensions={dimensions}
									onDeckConfigChange={onDeckConfigChange}
									market={market}
								/>
							)}

							{isActive && section.id === "roof" && supportsRoofControls && (
								<RoofConfigEditor
									roofConfig={roofConfig}
									onRoofConfigChange={onRoofConfigChange}
								/>
							)}

							{isActive && section.id === "site" && supportsSiteConditions && (
								<SiteConditionsEditor
									siteConditions={siteConditions}
									onSiteConditionsChange={onSiteConditionsChange}
								/>
							)}

							{isActive && section.id === "character" && (
								<FinishesEditor
									saunaType={saunaType}
									finishes={finishes}
									priceDeltas={finishPriceDeltas}
									market={market}
									onChange={onFinishesChange}
								/>
							)}

							{isActive && section.id === "equipment" && (
								<div className="inspector-section-body thermal-editor">
									<div className="input-row">
										<label htmlFor="sauna-lighting">{t.equipment_lighting}</label>
										<select id="sauna-lighting" value={thermalInputs.saunaLighting ?? "wall"}
											onChange={event => onThermalInputsChange({ ...thermalInputs, saunaLighting: event.target.value as ThermalInputs["saunaLighting"] })}>
											<option value="wall">{t.equipment_wallLight}</option>
											<option value="none">{t.equipment_noLight}</option>
										</select>
									</div>
									{supportsTwoRoomLayout && <>
										<label className="thermal-checkbox">
											<input type="checkbox" checked={thermalInputs.includesShower} disabled={!showerFits && !thermalInputs.includesShower}
												onChange={event => onThermalInputsChange({ ...thermalInputs, includesShower: event.target.checked })} />
											{t.equipment_shower}
										</label>
										{!showerFits && <p className="thermal-placement-note" role="status">{t.equipment_showerSpace}</p>}
										{thermalInputs.includesShower && <p className="thermal-placement-note">{t.equipment_showerScope}</p>}
									</>}
								</div>
							)}

							{isActive && section.id === "thermal" && (
								<ThermalInputsEditor
									saunaType={saunaType}
									dimensions={dimensions}
									roofConfig={roofConfig}
									partitionSplit={partitionSplit}
									thermalInputs={thermalInputs}
									runningCost={runningCost}
									heaterModelFit={heaterModelFit}
									onThermalInputsChange={onThermalInputsChange}
								/>
							)}
						</section>
					);
				})}
			</div>
		</div>
	);
}
